import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import useSessionHint from '../hooks/useSessionHint';
import { Menu, X, ArrowRight, Camera, FileText, Palette, Edit3, Tags, RotateCcw, Shuffle, Sparkles, Layers } from 'lucide-react';
import home from '../content/landing/home.json';
import '../pages/HomePage.css';

// Canonical marketing top-nav — the ONE bar every public page renders
// (homepage, feature landings, pricing, blogs, insights, products, templates).
// It is typographically self-contained (.lp-topnav sets the brand body font)
// so it looks identical regardless of the host page's root styles.
//
// "Products" is a direct link to /products — the dedicated page with imagery
// is the intuitive overview (stakeholder decision 2026-08-03; the old
// dropdown lives on in the mobile menu's product list).
const ICON_BY_NAME = {
  Camera, FileText, Palette, Layers, Sparkles, Edit3, Tags, RotateCcw, Shuffle,
};
const products = home.products.map((p) => ({ ...p, icon: ICON_BY_NAME[p.icon] }));

const CONTACT_URL = 'https://calendly.com/adstronaut/15min';

const NAV_LINKS = [
  { label: 'Products', to: '/products' },
  { label: 'Pricing', to: '/plans' },
  { label: 'Blogs', to: '/blogs' },
  { label: 'Insights', to: '/insights' },
];

const LandingTopNav = () => {
  // Public marketing nav — read sign-in state from the __client_uat cookie
  // instead of Clerk's useUser(), so this bar never boots clerk-js on landing
  // pages. See src/hooks/useSessionHint.js for why.
  const isSignedIn = useSessionHint();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const menuButtonRef = useRef(null);

  // Scroll detection for glassmorphism effect
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!mobileMenuOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setMobileMenuOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    const handleResize = () => {
      if (window.innerWidth >= 1020) setMobileMenuOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', handleResize);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', handleResize);
    };
  }, [mobileMenuOpen]);

  const linkClass = `font-medium transition-colors duration-300 whitespace-nowrap ${
    isScrolled ? 'text-white/90 hover:text-white' : 'text-gray-700 hover:text-gray-900'
  }`;

  return (
    <nav aria-label="Main navigation" className={`lp-topnav fixed w-full top-0 z-50 transition-all duration-300 ${
      isScrolled
        ? 'bg-black/50 backdrop-blur-xl border-b border-white/10'
        : 'bg-transparent'
    }`}>
      <div className="homepage-container">
        <div className="flex items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center flex-shrink-0" onClick={() => setMobileMenuOpen(false)} aria-label="Adstronaut AI home">
            <img
              src={isScrolled ? '/logo-white.png' : '/logo-black.png'}
              alt=""
              className="h-8 w-8 mr-2 transition-opacity duration-300"
              loading="eager"
            />
            <span className={`text-lg sm:text-xl font-bold transition-colors duration-300 whitespace-nowrap ${
              isScrolled ? 'text-white' : 'text-gray-900'
            }`}>Adstronaut AI</span>
          </Link>

          {/* Left Navigation - Desktop (collapses below 1020px) */}
          <div className="nav-links-1020">
            {NAV_LINKS.map((link) => (
              <Link key={link.to} to={link.to} className={linkClass}>
                {link.label}
              </Link>
            ))}
          </div>

          {/* Desktop CTA Buttons - pushed to right */}
          <div className="nav-ctas-1020">
            <a
              href={CONTACT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={`lp-nav-action px-5 py-2 rounded-full transition-all duration-300 font-medium border whitespace-nowrap ${
                isScrolled
                  ? 'border-white/30 text-white hover:bg-white/10'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-100'
              }`}
            >
              Contact Us
            </a>
            {isSignedIn ? (
              <Link
                to="/dashboard"
                className={`lp-nav-action px-6 py-2 rounded-full transition-all duration-300 font-medium whitespace-nowrap ${
                  isScrolled
                    ? 'bg-white text-black hover:bg-gray-100'
                    : 'bg-black text-white hover:bg-gray-800'
                }`}
              >
                Dashboard
              </Link>
            ) : (
              <Link
                to="/sign-in"
                className={`lp-nav-action px-6 py-2 rounded-full transition-all duration-300 font-medium whitespace-nowrap ${
                  isScrolled
                    ? 'bg-white text-black hover:bg-gray-100'
                    : 'bg-black text-white hover:bg-gray-800'
                }`}
              >
                Sign In
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            type="button"
            ref={menuButtonRef}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`nav-mobile-btn-1020 p-2 rounded-lg transition-colors duration-300 ${
              isScrolled
                ? 'text-white hover:bg-white/10'
                : 'text-gray-900 hover:bg-gray-100'
            }`}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileMenuOpen}
            aria-controls="marketing-mobile-menu"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" aria-hidden="true" /> : <Menu className="h-6 w-6" aria-hidden="true" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div id="marketing-mobile-menu" className="nav-mobile-menu-1020 bg-white border-b border-gray-100">
          <div className="px-5 py-4 space-y-3">
            {/* Products Section */}
            <div className="border-b border-gray-200 pb-3">
              <div className="text-gray-900 font-semibold mb-2">Products</div>
              <div className="space-y-1">
                {products.map((product) => {
                  const Icon = product.icon;
                  return (
                    <Link
                      key={product.path}
                      to={product.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className="w-full px-2 py-2 flex items-center hover:bg-gray-50 rounded-lg transition-colors"
                    >
                      {Icon && <Icon className="h-4 w-4 text-gray-600 mr-3 flex-shrink-0" aria-hidden="true" />}
                      <span className="text-gray-700 text-sm">{product.name}</span>
                    </Link>
                  );
                })}
                <Link
                  to="/products"
                  onClick={() => setMobileMenuOpen(false)}
                  className="w-full px-2 py-2 flex items-center hover:bg-gray-50 rounded-lg transition-colors"
                >
                  <ArrowRight className="h-4 w-4 text-gray-600 mr-3 flex-shrink-0" aria-hidden="true" />
                  <span className="text-gray-900 text-sm font-medium">View all products</span>
                </Link>
              </div>
            </div>

            {NAV_LINKS.filter((l) => l.to !== '/products').map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setMobileMenuOpen(false)}
                className="block w-full text-left py-2 text-gray-700 font-medium"
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-2 space-y-3">
              <a
                href={CONTACT_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMobileMenuOpen(false)}
                className="lp-nav-action block w-full px-6 py-2 text-center border border-gray-300 text-gray-700 rounded-full hover:bg-gray-100 transition font-medium"
              >
                Contact Us
              </a>
              {isSignedIn ? (
                <Link
                  to="/dashboard"
                  onClick={() => setMobileMenuOpen(false)}
                  className="lp-nav-action w-full px-6 py-2 bg-black text-white rounded-full hover:bg-gray-800 transition font-medium"
                >
                  Dashboard
                </Link>
              ) : (
                <Link
                  to="/sign-in"
                  onClick={() => setMobileMenuOpen(false)}
                  className="lp-nav-action w-full px-6 py-2 bg-black text-white rounded-full hover:bg-gray-800 transition font-medium"
                >
                  Sign In
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default LandingTopNav;
